/**
 * OAuth 2.1 authorization-code + PKCE client against the Maestro
 * authorization server. Fixed first-party contract (client "overture-web",
 * public, endpoints at known paths) — no runtime discovery.
 *
 * The in-flight PKCE state lives in localStorage, one entry per sign-in
 * keyed by its `state`, so concurrent sign-ins never clobber each other and
 * the callback can find its entry from ANY browsing context of this origin.
 * It used to live in sessionStorage (per tab), which Android breaks: a
 * home-screen (standalone) launch opens the cross-origin sign-in page in a
 * separate browser context whose sessionStorage is empty, and a tab that is
 * discarded while the password is typed comes back without it either — the
 * callback then failed with state_missing until "Try again".
 */

const AS_BASE = (process.env.REACT_APP_API_URL || "http://localhost:4000").replace(/\/+$/, "");
const CLIENT_ID = process.env.REACT_APP_OAUTH_CLIENT_ID || "overture-web";
const PKCE_PREFIX = "oauth.pkce.";
const LEGACY_PKCE_KEY = "oauth.pkce"; // sessionStorage, bundles before 0.33.2
const PKCE_MAX_AGE_MS = 10 * 60 * 1000;

const pkceKey = (state) => `${PKCE_PREFIX}${state}`;

const isStale = (entry) => !entry?.createdAt || Date.now() - entry.createdAt > PKCE_MAX_AGE_MS;

/** Drop abandoned sign-ins (older than the max age) so storage never grows. */
function prunePkce() {
  const stale = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith(PKCE_PREFIX)) continue;
    try {
      if (isStale(JSON.parse(localStorage.getItem(key)))) stale.push(key);
    } catch {
      stale.push(key);
    }
  }
  stale.forEach((key) => localStorage.removeItem(key));
}

/** True while at least one sign-in entry (of any age) exists. */
function anyPkceInFlight() {
  for (let i = 0; i < localStorage.length; i += 1) {
    if (localStorage.key(i)?.startsWith(PKCE_PREFIX)) return true;
  }
  return false;
}

function savePkce(entry) {
  prunePkce();
  localStorage.setItem(pkceKey(entry.state), JSON.stringify(entry));
}

function removePkce(state) {
  localStorage.removeItem(pkceKey(state));
}

/**
 * Read AND remove the entry for `state` (single-use, always). Falls back to
 * the pre-0.33.2 sessionStorage entry so a sign-in that started on the old
 * bundle and lands on this one still completes.
 */
function takePkce(state) {
  if (typeof state !== "string" || !state) return null;
  const raw = localStorage.getItem(pkceKey(state));
  removePkce(state);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const legacy = sessionStorage.getItem(LEGACY_PKCE_KEY);
  if (legacy) {
    sessionStorage.removeItem(LEGACY_PKCE_KEY);
    try {
      const entry = JSON.parse(legacy);
      if (entry?.state === state) return entry;
    } catch {
      /* corrupt */
    }
  }
  return null;
}

const redirectUri = () => `${window.location.origin}/auth/callback`;

function base64url(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function generateVerifier() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

export async function computeChallenge(verifier) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  return base64url(digest);
}

export function generateState() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

export function buildAuthorizeUrl(challenge, state) {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  });
  return `${AS_BASE}/authorize?${params.toString()}`;
}

const POPUP_NAME = "forte-oauth-login";
const POPUP_TIMEOUT_MS = 10 * 60 * 1000;
// Matches useIsMobileNav's MOBILE_NAV_MAX_WIDTH — the app-wide mobile boundary.
const REDIRECT_MAX_WIDTH = 768;

/**
 * Whether sign-in must use the single-tab full-page redirect instead of the
 * popup.
 *
 * Phones do NOT block window.open from a tap — they open a new tab, which
 * backgrounds the tab that owns the PKCE verifier and the postMessage
 * listener. iOS suspends (and under memory pressure evicts) that background
 * tab while the user types a password, so the relayed code can land on a
 * page that no longer exists; a home-screen (standalone) launch opens an
 * in-app browser with no opener at all. The redirect keeps everything in the
 * one tab. Desktop keeps the popup so the app stays on screen.
 */
export function shouldUseRedirectFlow() {
  if (typeof window === "undefined") return false;
  const mq = (q) => {
    try {
      return !!window.matchMedia?.(q)?.matches;
    } catch {
      return false;
    }
  };
  if (window.navigator?.standalone === true) return true; // iOS home-screen
  if (mq("(display-mode: standalone)")) return true;
  if (mq(`(max-width: ${REDIRECT_MAX_WIDTH}px)`)) return true;
  // Touch-primary devices (tablets in landscape exceed the width cut-off).
  return mq("(pointer: coarse)") && !mq("(pointer: fine)");
}

/**
 * Open the login popup SYNCHRONOUSLY (inside the click handler) so it survives
 * popup blockers — the URL is set later, once the PKCE challenge is computed.
 * Returns the window handle, or null if blocked.
 */
function openLoginPopup() {
  const w = 500;
  const h = 660;
  const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
  const popup = window.open(
    "about:blank",
    POPUP_NAME,
    `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );
  return popup && !popup.closed ? popup : null;
}

/**
 * Wait for the popup's callback page to postMessage the authorization result
 * back to us. Resolves { code, state } or rejects (error / user closed the
 * window / timeout). All listeners are torn down on settle.
 */
function waitForPopupMessage(popup) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn, arg) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      clearInterval(poll);
      clearTimeout(timer);
      fn(arg);
    };
    const onMessage = (event) => {
      // The callback runs on OUR origin (redirect_uri is the SPA), so this is
      // a same-origin message. Reject anything else.
      if (event.origin !== window.location.origin) return;
      const d = event.data;
      if (!d || d.source !== "forte-oauth") return;
      if (d.error) {
        finish(
          reject,
          oauthError(
            "oauth",
            d.error_description ||
              (d.error === "access_denied" ? "Sign-in was cancelled." : d.error)
          )
        );
        return;
      }
      if (d.code) finish(resolve, { code: d.code, state: d.state });
    };
    window.addEventListener("message", onMessage);
    const poll = setInterval(() => {
      if (popup.closed && !settled) {
        clearInterval(poll);
        // Grace period so an in-flight postMessage (sent just before close)
        // still wins the race.
        setTimeout(
          () =>
            finish(
              reject,
              oauthError("oauth", "Sign-in window was closed before completing.")
            ),
          600
        );
      }
    }, 400);
    const timer = setTimeout(
      () => finish(reject, oauthError("oauth", "Sign-in timed out.")),
      POPUP_TIMEOUT_MS
    );
  });
}

const HEALTH_TIMEOUT_MS = 6000;

/**
 * Probe the AS before handing the browser over to it.
 *
 * The popup (or the redirect tab) navigates cross-origin, so if the server is
 * down we can't see the failure: the popup shows the browser's own "can't be
 * reached" page and the opener sits waiting until the user closes it. A cheap
 * GET /health first turns that dead end into a readable message on the sign-in
 * page. Any failure — offline, refused, timeout, CORS — counts as unreachable.
 */
async function authServerReachable() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  try {
    const response = await fetch(`${AS_BASE}/health`, {
      cache: "no-store",
      signal: controller.signal,
    });
    return !!response?.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Begin sign-in — popup-preferred with automatic full-redirect fallback.
 *
 * Popup path: opens a small window, the user signs in there, its callback
 * relays the code back via postMessage, and THIS window (the opener) does the
 * token exchange. Resolves `{ via: "popup", tokens, returnTo }` — the caller
 * finalizes the session (see completeSignIn). The main app never navigates.
 *
 * Fallback path (popup blocked): full-page redirect to the AS; the returned
 * promise never resolves because the page unloads, and completion happens in
 * AuthCallbackPage instead.
 */
export async function beginLogin(returnTo = "/projects") {
  const verifier = generateVerifier();
  const state = generateState();
  savePkce({ verifier, state, returnTo, createdAt: Date.now() });

  // Must open BEFORE the await below to keep the user-gesture for the blocker.
  // Never opened on mobile / standalone — see shouldUseRedirectFlow().
  const popup = shouldUseRedirectFlow() ? null : openLoginPopup();
  const [challenge, reachable] = await Promise.all([
    computeChallenge(verifier),
    authServerReachable(),
  ]);

  if (!reachable) {
    removePkce(state);
    if (popup) {
      try {
        popup.close();
      } catch {
        /* already closed */
      }
    }
    throw oauthError(
      "network",
      "Can't reach the sign-in server. It may be restarting or offline — please try again in a few minutes."
    );
  }

  const url = buildAuthorizeUrl(challenge, state);

  if (!popup) {
    // Blocked, or a mobile/standalone device → robust full-page redirect.
    window.location.assign(url);
    return new Promise(() => {}); // page is unloading
  }

  popup.location = url;
  try {
    const { code, state: returnedState } = await waitForPopupMessage(popup);
    // The opener started the sign-in, so the exchange happens here (the entry
    // is in shared localStorage, but this window owns the pending promise).
    const { tokens, returnTo: rt } = await exchangeCode(code, returnedState);
    try {
      popup.close();
    } catch {
      /* already closed */
    }
    return { via: "popup", tokens, returnTo: rt };
  } catch (err) {
    try {
      popup.close();
    } catch {
      /* ignore */
    }
    throw err;
  }
}

/**
 * Called by the callback page when it is running INSIDE the login popup:
 * relay the authorization result to the opener and close. Returns true if it
 * handled a popup context (caller should render nothing further), false when
 * this is a normal full-redirect callback.
 */
export function relayPopupResultIfInPopup(params) {
  let inPopup = false;
  try {
    inPopup =
      !!window.opener &&
      window.opener !== window &&
      window.name === POPUP_NAME;
  } catch {
    inPopup = false;
  }
  if (!inPopup) return false;

  const message = {
    source: "forte-oauth",
    code: params.get("code") || undefined,
    state: params.get("state") || undefined,
    error: params.get("error") || undefined,
    error_description: params.get("error_description") || undefined,
  };
  try {
    window.opener.postMessage(message, window.location.origin);
  } catch {
    /* opener gone — nothing we can do from here */
  }
  window.close();
  return true;
}

/**
 * Typed OAuth error: err.code is "invalid_grant" | "oauth" | "network";
 * err.status is the HTTP status when the server answered.
 */
function oauthError(code, message, status) {
  const err = new Error(message);
  err.code = code;
  if (status !== undefined) err.status = status;
  return err;
}

async function tokenRequest(body) {
  let response;
  try {
    response = await fetch(`${AS_BASE}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(body).toString(),
    });
  } catch (e) {
    throw oauthError("network", `Could not reach the sign-in server: ${e.message}`);
  }
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* non-JSON error body */
  }
  if (!response.ok) {
    const code = data.error === "invalid_grant" ? "invalid_grant" : "oauth";
    throw oauthError(
      code,
      data.error_description || data.error || `HTTP ${response.status}`,
      response.status
    );
  }
  return data;
}

/**
 * Exchange the callback ?code for tokens. Looks the PKCE entry up by the
 * returned state, consumes (removes) it and checks its age. Returns
 * { tokens, returnTo }.
 */
export async function exchangeCode(code, returnedState) {
  const stored = takePkce(returnedState);
  if (!stored) {
    // A forged or foreign state finds no entry either — tell those apart
    // from "nothing was ever started" only for the error message.
    throw oauthError(
      "oauth",
      anyPkceInFlight()
        ? "state_mismatch: sign-in attempt is stale or forged"
        : "state_missing: no sign-in in progress — start again from the Sign in button"
    );
  }
  if (isStale(stored)) {
    throw oauthError("oauth", "state_mismatch: sign-in attempt is stale or forged");
  }
  const tokens = await tokenRequest({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    code,
    code_verifier: stored.verifier,
    redirect_uri: redirectUri(),
  });
  return { tokens, returnTo: stored.returnTo || "/projects" };
}

/** Refresh grant. Throws {code:"invalid_grant"} when the session is dead. */
export async function refreshGrant(refreshToken) {
  return tokenRequest({
    grant_type: "refresh_token",
    client_id: CLIENT_ID,
    refresh_token: refreshToken,
  });
}

/** Best-effort revocation (never throws). */
export async function revoke(refreshToken) {
  if (!refreshToken) return;
  try {
    await fetch(`${AS_BASE}/revoke`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        token: refreshToken,
        token_type_hint: "refresh_token",
        client_id: CLIENT_ID,
      }).toString(),
    });
  } catch {
    /* best-effort */
  }
}

/**
 * AS-session logout URL. Explicit logout NAVIGATES here (top-level, so the
 * sameSite=lax cookie is sent) to kill the Maestro login cookie too.
 */
export function buildLogoutUrl(postLogoutRedirect) {
  const params = new URLSearchParams({ redirect: postLogoutRedirect });
  return `${AS_BASE}/oauth/logout?${params.toString()}`;
}
